// Carousel-template send form — mounted by pages/Message/index.js (via the templateSend
// registry) in place of the library's own TemplateMessaging once a CAROUSEL template is picked
// from its list (its per-card body variables are a dropdown sourced from a fixed variable list,
// which this app has none of; see ../README.md for the swap-out plan). Built on SendPanelShell,
// which mirrors the library's own selected-template markup/classNames (TemplateMessaging.tsx,
// Components/Footer, Components/Carousel) so it reads as the same screen — CustomInput swaps in
// for that one Select-only field.
import { useMemo, useState } from 'react';
import { Tabs, TextField, Typography } from '@gupshup-frontend/gupshup-ts-components';
import SendPanelShell from '../shared/SendPanelShell';
import FreeTextInput from '../shared/FreeTextInput';
import { useReportValues } from '../shared/useReportValues';
import { validateFreeText, validateMedia, countBraceTokens } from '../shared/validators';
import { findComponent } from '../shared/savedJsonMatch';
import { buildSubmitData } from '../shared/submitData';
import {
  CAROUSEL_WIRE_SHAPE, activeCarouselWireShape, buildCarouselTemplateJson, buildCarouselVariables, parseCarouselTemplateJson,
} from './buildCarouselTemplateJson';
import './carousel.css';

function blankCardValues(carouselCards) {
  return (carouselCards || []).map((card) => {
    const bodyComponent = findComponent(card?.components, 'body');
    const bodyCount = countBraceTokens(bodyComponent?.text);
    return { media: '', body: Array.from({ length: bodyCount }, () => ''), buttons: {} };
  });
}

// The payload Save submits, from the values as they stand with no checks: blank and half-typed values go out as they are.
function buildCarouselPayload({
  template, channelId, orgId, outerBody, cardValues, shape,
}) {
  const outerVarCount = countBraceTokens(template?.details?.data);
  return buildSubmitData({
    template,
    channelId,
    orgId,
    params: Array.from({ length: outerVarCount }, (_, i) => outerBody[i] ?? ''),
    headerParams: [],
    // Total, because this runs on mount: a template with no type used to fail only at Save.
    messageType: String(template.details.type ?? '').toLowerCase(),
    waTemplateJson: buildCarouselTemplateJson(template?.details?.carouselCards || [], { cards: cardValues }, shape),
    subType: 'CAROUSEL',
  });
}

/**
 * @typedef {object} CarouselSendPanelProps
 * @property {{ details: object }} template - list-row template; details.carouselCards/id/body/type/data are read
 * @property {string} channelId
 * @property {string} orgId
 * @property {(data: object) => boolean} onSubmit - see ../shared/submitData.js for the payload shape
 * @property {() => void} onBack
 * @property {{ params?: string[], waTemplateJson?: object }|null} restored - null for a fresh pick
 * @property {boolean} [disabled]
 * @property {{ footerComponent?: import('react').ReactNode, sendButtonText?: string }} [customisations]
 * @property {(data: object) => void} [onValuesChange] - called after mount and whenever the payload's content changes,
 *   with the object Save would pass to onSubmit, built without validation (blank and half-typed values go out as they are)
 */

/**
 * @param {CarouselSendPanelProps} props
 * @returns {JSX.Element}
 */
const CarouselSendPanel = ({
  template, channelId, orgId, onSubmit, onBack, restored, disabled, customisations, onValuesChange,
}) => {
  const carouselCards = template?.details?.carouselCards || [];
  const outerVarCount = countBraceTokens(template?.details?.data);

  const [outerBody, setOuterBody] = useState(() => (restored
    ? Array.from({ length: outerVarCount }, (_, i) => (restored.params || [])[i] ?? '')
    : Array.from({ length: outerVarCount }, () => '')));
  const [cardValues, setCardValues] = useState(() => (restored
    ? parseCarouselTemplateJson(restored.waTemplateJson, carouselCards).cards
    : blankCardValues(carouselCards)));
  const [errors, setErrors] = useState({});
  const [activeCard, setActiveCard] = useState(0);
  const [errorToastOpen, setErrorToastOpen] = useState(false);
  // Bumped by a Save that passes its checks. The library clears its template there (TemplateMessaging.tsx:275-288), which
  // unmounts the preview; remounting it here brings a media URL that does not load back to the preview's "Sample Media".
  const [previewKey, setPreviewKey] = useState(0);

  // Read fresh on every render, not cached — the whole point is picking up a change made in this
  // browser's console without a redeploy.
  const activeShape = activeCarouselWireShape();
  const showWireShapeNote = activeShape !== CAROUSEL_WIRE_SHAPE;

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setOuterVar = (index, value) => {
    setOuterBody((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    clearError(`outer.${index}`);
  };

  const setMedia = (cardIndex, value) => {
    setCardValues((prev) => prev.map((c, i) => (i === cardIndex ? { ...c, media: value } : c)));
    clearError(`media.${cardIndex}`);
  };

  const setCardBodyVar = (cardIndex, varIndex, value) => {
    setCardValues((prev) => prev.map((c, i) => (
      i === cardIndex ? { ...c, body: c.body.map((v, j) => (j === varIndex ? value : v)) } : c
    )));
    clearError(`body.${cardIndex}.${varIndex}`);
  };

  const setButtonSuffix = (cardIndex, buttonIndex, value) => {
    setCardValues((prev) => prev.map((c, i) => (
      i === cardIndex ? { ...c, buttons: { ...c.buttons, [buttonIndex]: value } } : c
    )));
    clearError(`button.${cardIndex}.${buttonIndex}`);
  };

  const cardHasError = (cardIndex) => Object.keys(errors).some((key) => key === `media.${cardIndex}`
    || key.startsWith(`body.${cardIndex}.`) || key.startsWith(`button.${cardIndex}.`));

  const handleSave = () => {
    const nextErrors = {};
    let firstErrorCard = null;

    for (let i = 0; i < outerVarCount; i += 1) {
      const err = validateFreeText(outerBody[i]);
      if (err) nextErrors[`outer.${i}`] = err;
    }

    carouselCards.forEach((card, cardIndex) => {
      const values = cardValues[cardIndex] || { media: '', body: [], buttons: {} };
      const headerFormat = findComponent(card.components, 'header')?.format || template?.details?.type;
      const mediaErr = validateMedia(values.media, headerFormat);
      if (mediaErr) {
        nextErrors[`media.${cardIndex}`] = mediaErr;
        if (firstErrorCard === null) firstErrorCard = cardIndex;
      }

      const bodyCount = countBraceTokens(findComponent(card.components, 'body')?.text);
      for (let i = 0; i < bodyCount; i += 1) {
        const err = validateFreeText(values.body[i]);
        if (err) {
          nextErrors[`body.${cardIndex}.${i}`] = err;
          if (firstErrorCard === null) firstErrorCard = cardIndex;
        }
      }

      const buttons = findComponent(card.components, 'buttons')?.buttons || [];
      buttons.forEach((button, buttonIndex) => {
        if (button?.type === 'URL' && button?.urlType !== 'STATIC') {
          const err = validateFreeText(values.buttons[buttonIndex]);
          if (err) {
            nextErrors[`button.${cardIndex}.${buttonIndex}`] = err;
            if (firstErrorCard === null) firstErrorCard = cardIndex;
          }
        }
      });
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      if (firstErrorCard !== null) setActiveCard(firstErrorCard);
      // Same copy as the library's own Footer isError toast (Components/Footer/index.tsx) — kept
      // in addition to the inline errors below, which the library's Select-only fields never
      // needed (a dropdown can't hold an invalid value; our free-text fields can).
      setErrorToastOpen(true);
      return;
    }

    setPreviewKey((key) => key + 1);
    // The shape is read fresh at Save, so a console switch applies without a re-render; a render reads it once.
    onSubmit(buildCarouselPayload({
      template, channelId, orgId, outerBody, cardValues, shape: activeCarouselWireShape(),
    }));
  };

  // Rebuilt only when an input changes, not on every render: a tab switch, a toast or a host re-render leaves it alone.
  const payload = useMemo(() => buildCarouselPayload({
    template, channelId, orgId, outerBody, cardValues, shape: activeShape,
  }), [template, channelId, orgId, outerBody, cardValues, activeShape]);
  useReportValues(payload, onValuesChange);

  // The preview reads the payload's own wire JSON, so it can never drift from what Save would send.
  const cardVariables = buildCarouselVariables(payload.waTemplateJson?.components?.[0]?.cards);
  // Blank slots preview as their own {{n}} token, same as TemplateMessaging's templateParamsObj
  // default before anything is typed.
  const outerVariables = Array.from({ length: outerVarCount }, (_, i) => (
    (outerBody[i] && outerBody[i].trim()) ? outerBody[i] : `{{${i + 1}}}`
  ));

  // Mirrors TemplateMessaging's own Box className condition (isVariablePresent || category !==
  // "TEXT"); a carousel's header/body variables live on its cards, not top-level, so there's no
  // top-level header count to add in. SendPanelShell has no template-shape knowledge, so the
  // resolved className is what's passed down, not the condition itself.
  const showSlContainer = outerVarCount > 0 || String(template?.details?.type) !== 'TEXT';

  return (
    <SendPanelShell
      leftClassName={showSlContainer ? 'nic-tm-sl-template-container' : undefined}
      previewKey={previewKey}
      previewConfig={{
        templateObj: template?.details,
        showHeading: true,
        headingName: 'Preview',
        variables: outerVariables,
        card_variables: cardVariables,
        width: '100%',
        height: '100%',
      }}
      onBack={onBack}
      onSave={handleSave}
      customisations={customisations}
      disabled={disabled}
      showInvalidToast={errorToastOpen}
      onCloseInvalidToast={() => setErrorToastOpen(false)}
      footerExtra={showWireShapeNote ? (
        <Typography variant="caption" className="br-carousel-send-skip-note" data-testid="bloomreach-carousel-send-wire-shape-note">
          {`Testing: WhatsApp card format '${activeShape}' (set in this browser).`}
        </Typography>
      ) : null}
    >
      {outerVarCount > 0 && (
        <>
          <p className="nic-tm-sub-header-padding">Body</p>
          {Array.from({ length: outerVarCount }).map((_, index) => (
            <FreeTextInput
              key={`outer-${index}`}
              style={{ marginBottom: '16px', display: 'flex', gap: '18px' }}
              label={`Enter content for {{${index + 1}}}`}
              value={outerBody[index] ?? ''}
              onChange={(value) => setOuterVar(index, value)}
              helperText={errors[`outer.${index}`] || ''}
            />
          ))}
        </>
      )}

      <div className="Carousel-margin nic-tm-sub-header-padding">Carousel details</div>
      <div style={{ maxWidth: '500px' }}>
        <Tabs
          value={activeCard}
          handleTabChange={(event, value) => setActiveCard(value)}
          tabsType="contained"
          contentPadding="0px"
          data={carouselCards.map((card, index) => {
            const values = cardValues[index] || { media: '', body: [], buttons: {} };
            const headerComponent = findComponent(card.components, 'header');
            const bodyComponent = findComponent(card.components, 'body');
            const bodyCount = countBraceTokens(bodyComponent?.text);
            const buttons = findComponent(card.components, 'buttons')?.buttons || [];
            const mediaType = headerComponent?.format || template.details.type;

            return {
              label: (
                <span className="br-carousel-send-tab-label">
                  {`Carousel ${index + 1}`}
                  {cardHasError(index) && <span className="br-carousel-send-tab-error" />}
                </span>
              ),
              'data-testid': `bloomreach-carousel-send-tab-${index}`,
              content: (
                <div key={index} data-testid={`bloomreach-carousel-send-card-${index}`}>
                  {/* Same wrapper/label/props shape as CarouselComponent's own MediaComponent
                      (Components/Carousel/index.tsx) — value/onChange/error/helperText added,
                      since that field has no format or emptiness validation of its own. */}
                  <div className="nic-carousel-media-component">
                    <TextField
                      fullWidth
                      color="primary"
                      label={`Enter ${String(mediaType || '').toLowerCase()} URL`}
                      required
                      value={values.media}
                      disabled={disabled}
                      error={Boolean(errors[`media.${index}`])}
                      helperText={errors[`media.${index}`]
                        || `An https link to a .jpg, .jpeg or .png image (.mp4 for video), or a customer attribute such as {customer.${mediaType === 'VIDEO' ? 'product_video' : 'product_image'}}`}
                      onChange={(e) => setMedia(index, e.target.value)}
                    />
                  </div>

                  {bodyCount > 0 && (
                    // Same wrapper as CarouselComponent's own per-variable div; a free-text field
                    // stands in for its Select, which only offers a fixed attribute list this app doesn't have.
                    <div style={{ marginTop: '24px' }}>
                      {Array.from({ length: bodyCount }).map((_, varIndex) => (
                        <FreeTextInput
                          key={`card-${index}-body-${varIndex}`}
                          style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column' }}
                          label={`Enter content for {{${varIndex + 1}}}`}
                          value={values.body[varIndex] || ''}
                          onChange={(value) => setCardBodyVar(index, varIndex, value)}
                          helperText={errors[`body.${index}.${varIndex}`] || ''}
                        />
                      ))}
                      <br />
                    </div>
                  )}

                  {/* Same skip rule and wrapper as CarouselComponent's own ButtonComponent;
                      static/quick-reply buttons render nothing there either — the preview
                      panel is where they're visible. */}
                  {buttons.map((button, buttonIndex) => {
                    if (button?.type === 'QUICK_REPLY'
                      || (button?.type === 'URL' && button?.urlType === 'STATIC')
                      || button?.type === 'PHONE_NUMBER') return null;
                    return (
                      <div style={{ marginBottom: '16px' }} key={`card-${index}-button-${buttonIndex}`}>
                        <TextField
                          fullWidth
                          color="primary"
                          label="Button URL Params"
                          required
                          helperText={errors[`button.${index}.${buttonIndex}`]
                            || 'The dynamic suffix appended to the button URL, e.g. developer/home, or a customer attribute such as {customer.referral_code}.'}
                          error={Boolean(errors[`button.${index}.${buttonIndex}`])}
                          value={values.buttons[buttonIndex] || ''}
                          disabled={disabled}
                          onChange={(e) => setButtonSuffix(index, buttonIndex, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              ),
            };
          })}
        />
      </div>
    </SendPanelShell>
  );
};

export default CarouselSendPanel;
